import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { formatCLP, apiError } from "../../lib/format";
import { simulate } from "../../lib/loanMath";
import { toast } from "sonner";
import {
  Calculator,
  HandCoins,
  Percent,
  CalendarBlank,
  TrendUp,
  PaperPlaneTilt,
  Printer,
  ArrowClockwise,
  UserCircle,
} from "@phosphor-icons/react";

const PRESETS = [
  { label: "Consumo corto", amount: 500000, cuotas: 12, rate: 1.4 },
  { label: "Consumo medio", amount: 2000000, cuotas: 24, rate: 1.2 },
  { label: "Consumo largo", amount: 5000000, cuotas: 48, rate: 1.0 },
  { label: "Crédito auto", amount: 10000000, cuotas: 60, rate: 0.85 },
];

export default function LoanSimulator() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    amount: 1000000,
    cuotas: 12,
    rate: 1.2,
  });
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState(params.get("client_id") || "");
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data));
  }, []);

  const sim = useMemo(
    () => simulate(form.amount, form.rate, form.cuotas),
    [form.amount, form.rate, form.cuotas]
  );

  const applyPreset = (p) =>
    setForm({ amount: p.amount, cuotas: p.cuotas, rate: p.rate });

  const sendOffer = async () => {
    if (!clientId) return toast.error("Selecciona un cliente");
    if (form.amount < 500000) return toast.error("Monto mínimo $ 500.000");
    if (form.cuotas < 3 || form.cuotas > 220) return toast.error("Cuotas entre 3 y 220");
    setSending(true);
    try {
      await api.post("/loans/offer", {
        client_id: clientId,
        amount: Number(form.amount),
        cuotas: Number(form.cuotas),
        rate: Number(form.rate),
        comment,
      });
      toast.success("Oferta enviada al cliente");
      setComment("");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const printSim = () => window.print();

  return (
    <div className="space-y-8" data-testid="loan-simulator">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-mono mb-2">Asesor · Herramientas</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter flex items-center gap-3">
            <Calculator size={40} weight="duotone" className="text-forest" />
            Simulador de crédito
          </h1>
          <p className="text-inkMuted text-sm mt-2 max-w-lg">
            Ajusta monto, cuotas e interés mensual. Cuando el plan te convenza, envíalo como oferta al cliente.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setForm({ amount: 1000000, cuotas: 12, rate: 1.2 })}
            className="h-11 px-4 border border-hairline text-sm flex items-center gap-2 hover:bg-bg"
            data-testid="sim-reset"
          >
            <ArrowClockwise size={14} weight="bold" /> Reiniciar
          </button>
          <button
            onClick={printSim}
            className="h-11 px-4 border border-hairline text-sm flex items-center gap-2 hover:bg-bg"
            data-testid="sim-print"
          >
            <Printer size={14} weight="bold" /> Imprimir
          </button>
        </div>
      </header>

      {/* Presets */}
      <div className="flex flex-wrap gap-2" data-testid="sim-presets">
        <span className="label-mono py-2 mr-2">Plantillas rápidas</span>
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => applyPreset(p)}
            className="h-9 px-3 border border-hairline text-xs font-mono uppercase tracking-widest hover:bg-forest hover:text-bg hover:border-forest transition-colors"
            data-testid={`preset-${p.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* INPUTS */}
        <div className="lg:col-span-3 bento p-8 space-y-8">
          {/* Monto */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="label-mono flex items-center gap-2">
                <HandCoins size={14} weight="duotone" /> Monto solicitado
              </label>
              <span className="font-mono text-sm text-burnt">{formatCLP(form.amount)}</span>
            </div>
            <input
              data-testid="sim-amount"
              type="number"
              min="500000"
              step="50000"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              className="w-full h-20 border border-hairline bg-surface px-4 font-mono text-4xl focus:outline-none focus:border-forest"
            />
            <input
              type="range"
              min="500000"
              max="50000000"
              step="50000"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })}
              className="w-full mt-3 accent-forest"
              data-testid="sim-amount-range"
            />
            <div className="flex justify-between label-mono mt-1">
              <span>$ 500K</span>
              <span>$ 10M</span>
              <span>$ 25M</span>
              <span>$ 50M</span>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {/* Cuotas */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="label-mono flex items-center gap-2">
                  <CalendarBlank size={14} weight="duotone" /> Cuotas
                </label>
                <span className="font-mono text-sm">{form.cuotas} meses</span>
              </div>
              <input
                data-testid="sim-cuotas"
                type="number"
                min="3"
                max="220"
                value={form.cuotas}
                onChange={(e) => setForm({ ...form, cuotas: Math.max(3, Math.min(220, Number(e.target.value) || 3)) })}
                className="w-full h-14 border border-hairline bg-surface px-4 font-mono text-2xl focus:outline-none focus:border-forest"
              />
              <input
                type="range"
                min="3"
                max="220"
                value={form.cuotas}
                onChange={(e) => setForm({ ...form, cuotas: Number(e.target.value) })}
                className="w-full mt-3 accent-forest"
                data-testid="sim-cuotas-range"
              />
              <div className="flex justify-between label-mono mt-1">
                <span>3</span><span>60</span><span>120</span><span>220</span>
              </div>
            </div>

            {/* Tasa */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="label-mono flex items-center gap-2">
                  <Percent size={14} weight="duotone" /> Interés mensual
                </label>
                <span className="font-mono text-sm">{form.rate.toFixed(2)} %</span>
              </div>
              <input
                data-testid="sim-rate"
                type="number"
                min="0"
                step="0.05"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: Math.max(0, Number(e.target.value) || 0) })}
                className="w-full h-14 border border-hairline bg-surface px-4 font-mono text-2xl focus:outline-none focus:border-forest"
              />
              <input
                type="range"
                min="0"
                max="5"
                step="0.05"
                value={form.rate}
                onChange={(e) => setForm({ ...form, rate: Number(e.target.value) })}
                className="w-full mt-3 accent-forest"
                data-testid="sim-rate-range"
              />
              <div className="flex justify-between label-mono mt-1">
                <span>0%</span><span>1%</span><span>2%</span><span>5%</span>
              </div>
            </div>
          </div>

          {/* Offer sender */}
          <div className="border-t border-hairline pt-6">
            <h3 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
              <PaperPlaneTilt size={18} weight="duotone" /> Enviar oferta al cliente
            </h3>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="label-mono block mb-1.5 flex items-center gap-1"><UserCircle size={12} /> Cliente</label>
                <select
                  data-testid="sim-client"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  className="w-full h-11 border border-hairline bg-surface px-3 text-sm"
                >
                  <option value="">— Selecciona un cliente —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} · {c.rut}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Comentario (opcional)</label>
                <input
                  data-testid="sim-comment"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Ej: Oferta preferencial"
                  className="w-full h-11 border border-hairline bg-surface px-3 text-sm"
                />
              </div>
            </div>
            <button
              onClick={sendOffer}
              disabled={sending}
              data-testid="sim-send-offer"
              className="h-12 px-6 bg-burnt text-bg font-semibold flex items-center gap-2 hover:bg-burnt-hover disabled:opacity-50"
            >
              <PaperPlaneTilt size={16} weight="bold" /> {sending ? "Enviando…" : "Enviar oferta"}
            </button>
          </div>
        </div>

        {/* RESULT SUMMARY */}
        <aside className="lg:col-span-2 bg-forest text-bg p-8 flex flex-col" data-testid="sim-summary">
          <div className="label-mono text-bg/60 mb-3">// Resultado simulado</div>
          <h3 className="font-display text-2xl font-bold mb-6">Plan sugerido</h3>

          <div className="space-y-5 flex-1">
            <Metric label="Cuota mensual" value={formatCLP(Math.round(sim.monthly))} large />
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-bg/20">
              <Metric label="Total a pagar" value={formatCLP(Math.round(sim.total))} />
              <Metric label="Intereses totales" value={formatCLP(Math.round(sim.interest))} accent />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-bg/20">
              <Metric label="Capital solicitado" value={formatCLP(sim.principal)} />
              <Metric label="Plazo" value={`${sim.cuotas} meses`} />
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-bg/20">
              <Metric label="Tasa mensual" value={`${sim.ratePct.toFixed(2)} %`} />
              <Metric label="CAE estimado" value={`${((Math.pow(1 + sim.ratePct / 100, 12) - 1) * 100).toFixed(2)} %`} help="anual compuesto" />
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-bg/20 text-xs text-bg/60 font-mono flex items-center gap-2">
            <TrendUp size={14} weight="duotone" /> Sistema francés · Cuota fija
          </div>
        </aside>
      </div>

      {/* AMORTIZATION TABLE */}
      <div className="bento" data-testid="sim-amortization">
        <div className="px-6 py-4 border-b border-hairline flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Tabla de amortización</h2>
          <span className="label-mono">{sim.rows.length} cuotas</span>
        </div>
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr className="label-mono border-b border-hairline">
                <th className="text-left px-6 py-3">#</th>
                <th className="text-right px-6 py-3">Cuota</th>
                <th className="text-right px-6 py-3">Interés</th>
                <th className="text-right px-6 py-3">Capital</th>
                <th className="text-right px-6 py-3">Saldo</th>
              </tr>
            </thead>
            <tbody>
              {sim.rows.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-inkMuted text-sm">Ingresa valores válidos.</td></tr>
              )}
              {sim.rows.map((r) => (
                <tr key={r.i} className="border-b border-hairline hover:bg-bg">
                  <td className="px-6 py-2 font-mono text-xs text-inkMuted">{String(r.i).padStart(2, "0")}</td>
                  <td className="px-6 py-2 text-right font-mono">{formatCLP(Math.round(r.payment))}</td>
                  <td className="px-6 py-2 text-right font-mono text-burnt">{formatCLP(Math.round(r.interest))}</td>
                  <td className="px-6 py-2 text-right font-mono text-success">{formatCLP(Math.round(r.principal))}</td>
                  <td className="px-6 py-2 text-right font-mono">{formatCLP(Math.round(r.balance))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, large, accent, help }) {
  return (
    <div>
      <div className="label-mono text-bg/60">{label}{help && <span className="normal-case tracking-normal ml-1 text-bg/40">· {help}</span>}</div>
      <div className={`font-mono font-medium mt-1 ${large ? "text-4xl" : "text-xl"} ${accent ? "text-burnt" : ""}`}>
        {value}
      </div>
    </div>
  );
}
